import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDateTimeBs } from "@/lib/format";
import { throwIfNotOk } from "@/lib/api-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PayoutAvatar } from "@/components/admin/payouts/primitives";
import { CircleNotch, Copy, LinkSimple, Warning } from "@phosphor-icons/react";
import type { TherapistSubscriptionView } from "@/types";

interface Props {
  therapist: TherapistSubscriptionView;
  onClose: () => void;
}

interface GenerateResult {
  url: string;
  expiresAt: string;
}

const PLAN_PLACEHOLDER = "__none__";

export function GenerateLinkDialog({ therapist, onClose }: Props) {
  const qc = useQueryClient();
  const [planType, setPlanType] = useState<"" | "semiannual" | "annual">("");
  // Empty = server uses the TTL from subscription settings.
  const [ttlHours, setTtlHours] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleGenerate() {
    const ttlTrimmed = ttlHours.trim();
    const ttl = ttlTrimmed === "" ? undefined : Number(ttlTrimmed);
    if (ttl !== undefined && (!Number.isInteger(ttl) || ttl < 1 || ttl > 720)) {
      setError("Trajanje linka mora biti cijeli broj sati od 1 do 720, ili ostavite polje prazno.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/therapists/${therapist.therapistId}/payment-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planType: planType || undefined,
          ttlHours: ttl,
          sendEmail,
          notes: notes.trim() || undefined,
        }),
      });
      await throwIfNotOk(res, "Greška pri generisanju linka.");
      const body = (await res.json()) as GenerateResult;
      setResult(body);
      // Generating a link writes a pending ledger row and supersedes the
      // therapist's earlier ones, so the ledger table and the pending-transfer
      // badge are both stale the moment this returns.
      qc.invalidateQueries({ queryKey: ["admin", "subscription-ledger"] });
      qc.invalidateQueries({ queryKey: ["admin", "subscription-overview"] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={LinkSimple}
          title="Generiši payment link"
          description="Kreirajte vremenski ograničen link za terapeuta."
        />
      </DialogHeader>

      <DialogBody className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-xl bg-muted/40 px-3.5 py-3">
          <PayoutAvatar name={therapist.name} tone="primary" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">
              {therapist.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {therapist.email}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 text-[13px] leading-snug text-rose-800 ring-1 ring-inset ring-rose-200/70">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {!result ? (
          <div className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label>Plan (opcionalno)</Label>
              <Select
                value={planType === "" ? PLAN_PLACEHOLDER : planType}
                onValueChange={(v) => setPlanType(v === PLAN_PLACEHOLDER ? "" : (v as "semiannual" | "annual"))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PLAN_PLACEHOLDER}>Neka terapeut izabere</SelectItem>
                  <SelectItem value="semiannual">Polugodišnja</SelectItem>
                  <SelectItem value="annual">Godišnja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Trajanje linka (sati)</Label>
              <Input
                type="number"
                min={1}
                max={720}
                step={1}
                placeholder="Prema postavkama pretplate"
                value={ttlHours}
                onChange={(e) => setTtlHours(e.target.value)}
              />
            </div>
            <Label className="font-normal">
              <Checkbox
                checked={sendEmail}
                onCheckedChange={(c) => setSendEmail(c === true)}
              />
              Pošalji email terapeutu
            </Label>
            <div className="space-y-1.5">
              <Label>Napomena (interno)</Label>
              <Input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-inset ring-emerald-200/70">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-emerald-900">
                <LinkSimple className="size-4" />
                Link generisan
              </div>
              <div className="break-all rounded-lg border border-emerald-100 bg-white p-2.5 font-mono text-xs text-foreground">
                {result.url}
              </div>
              <div className="mt-2 text-xs text-emerald-700">
                Ističe: {formatDateTimeBs(result.expiresAt)}
              </div>
            </div>
            <div className="border-t border-border/40 pt-3 text-xs text-muted-foreground">
              Pošalji ovaj link terapeutu putem Vibera, WhatsApp-a ili SMS-a.
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        {!result ? (
          <>
            <Button variant="outline" onClick={onClose}>Otkaži</Button>
            <Button onClick={handleGenerate} disabled={submitting}>
              {submitting ? (
                <>
                  <CircleNotch className="size-4 animate-spin" />
                  Generišem...
                </>
              ) : (
                <>
                  <LinkSimple className="size-4" />
                  Generiši
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(result.url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
            >
              <Copy className="size-4" />
              {copied ? "Kopirano ✓" : "Kopiraj link"}
            </Button>
            <Button onClick={onClose}>Gotovo</Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
